import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangleIcon, ArrowLeftIcon } from "lucide-react";
import { useState } from "react";
import { useBulkCreateClassSessions } from "../api/queries";
import { BulkCreateSessionsForm } from "../ui/BulkCreateSessionsForm";

const toHHmm = (s: string) => (s.length >= 5 ? s.slice(0, 5) : s);

export function BulkCreateSessionsPage() {
  const nav = useNavigate({ from: "/app/classes/sessions/bulk-create" });
  const bulkCreate = useBulkCreateClassSessions();
  const [conflicts, setConflicts] = useState<
    Array<{
      date: string;
      startTime: string;
      endTime: string;
      roomName: string;
      message: string;
    }>
  >([]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຈັດຕາຕະລາງຄລາດສ໌", to: "/app/classes/sessions" },
            { label: "ສ້າງຫຼາຍຊົ່ວໂມງຮຽນ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <h2 className="flex items-center font-bold text-2xl tracking-tight">
            ສ້າງຫຼາຍຊົ່ວໂມງຮຽນ
          </h2>

          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/classes/sessions" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div className="mt-6">
          <BulkCreateSessionsForm
            onSubmit={async (vals) => {
              try {
                setConflicts([]);
                const result = await bulkCreate.mutateAsync(vals);
                if (
                  result.conflictingDates.length > 0 &&
                  result.createdCount === 0
                ) {
                  setConflicts(
                    result.conflictingDates.map((c) => ({
                      date: c.date,
                      startTime: c.startTime,
                      endTime: c.endTime,
                      roomName: c.roomName,
                      message: c.message,
                    })),
                  );
                  return;
                }
                nav({ to: "/app/classes/sessions" });
              } catch {
                // Error handled by mutation
              }
            }}
            onCancel={() => nav({ to: "/app/classes/sessions" })}
            submitting={bulkCreate.isPending}
          />
        </div>

        {conflicts.length > 0 && (
          <div className="mt-6 rounded-xl border border-red-200 bg-red-50/50 dark:border-red-900/50 dark:bg-red-950/20">
            <div className="flex items-center gap-2 border-red-200 border-b px-4 py-3 dark:border-red-900/50">
              <AlertTriangleIcon className="size-4 shrink-0 text-red-600 dark:text-red-500" />
              <h3 className="font-semibold text-red-900 dark:text-red-100">
                ພົບຄວາມຂັດແຍ່ງ — ບໍ່ສາມາດສ້າງ ຊົ່ວໂມງຮຽນ ໄດ້
              </h3>
            </div>
            <div className="overflow-x-auto p-4">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-red-200 border-b text-left text-muted-foreground dark:border-red-900/50">
                    <th className="pr-4 pb-2 font-medium">ວັນທີ</th>
                    <th className="pr-4 pb-2 font-medium">ເວລາ</th>
                    <th className="pr-4 pb-2 font-medium">ຫ້ອງ</th>
                    <th className="pb-2 font-medium">ເຫດຜົນ</th>
                  </tr>
                </thead>
                <tbody>
                  {conflicts.map((c) => (
                    <tr
                      key={`${c.date}-${c.startTime}-${c.endTime}-${c.roomName}`}
                      className="border-red-100 border-b last:border-0 dark:border-red-900/30"
                    >
                      <td className="py-2 pr-4 font-medium">{c.date}</td>
                      <td className="py-2 pr-4">
                        {toHHmm(c.startTime)}–{toHHmm(c.endTime)}
                      </td>
                      <td className="py-2 pr-4">{c.roomName}</td>
                      <td className="py-2 text-muted-foreground">
                        {c.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Main>
    </>
  );
}
